import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { MapPin, Phone, Mail, Clock, Send, Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSuccess(true);
      toast.success("Message sent! We'll get back to you soon.");
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="min-h-screen bg-[#0C0C0E] pt-24">
      {/* Hero Section */}
      <section className="relative py-16 md:py-24">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-32 right-0 w-96 h-96 bg-pomegranate/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-accent text-saffron text-xl mb-3"
          >
            Get in Touch
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-heading text-5xl md:text-6xl lg:text-7xl text-white mb-6"
          >
            Contact <span className="italic text-saffron">Us</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="font-body text-white/50 text-lg max-w-xl"
          >
            Have questions or feedback? We'd love to hear from you.
          </motion.p>
        </div>
      </section>

      {/* Main Content */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 pb-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-24">
          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-8"
          >
            <div className="grid sm:grid-cols-2 gap-6">
              {/* Location Card */}
              <div className="bg-surface/50 border border-white/5 rounded-xl p-6 hover:border-saffron/20 transition-colors">
                <div className="w-12 h-12 rounded-full bg-saffron/10 flex items-center justify-center mb-4">
                  <MapPin className="w-6 h-6 text-saffron" />
                </div>
                <h3 className="font-heading text-white text-lg mb-2">Visit Us</h3>
                <p className="font-body text-white/50 text-sm">
                  7854 Michael's Rd<br />
                  Beverly Hills, CA
                </p>
              </div>

              {/* Phone Card */}
              <div className="bg-surface/50 border border-white/5 rounded-xl p-6 hover:border-saffron/20 transition-colors">
                <div className="w-12 h-12 rounded-full bg-saffron/10 flex items-center justify-center mb-4">
                  <Phone className="w-6 h-6 text-saffron" />
                </div>
                <h3 className="font-heading text-white text-lg mb-2">Call Us</h3>
                <a href="tel:+1234567890" className="font-body text-white/50 text-sm hover:text-saffron transition-colors">
                  +1 (234) 567-890
                </a>
              </div>

              {/* Email Card */}
              <div className="bg-surface/50 border border-white/5 rounded-xl p-6 hover:border-saffron/20 transition-colors">
                <div className="w-12 h-12 rounded-full bg-saffron/10 flex items-center justify-center mb-4">
                  <Mail className="w-6 h-6 text-saffron" />
                </div>
                <h3 className="font-heading text-white text-lg mb-2">Email Us</h3>
                <a href="mailto:Operations@virtuosogenai.com" className="font-body text-white/50 text-sm hover:text-saffron transition-colors">
                  Operations@virtuosogenai.com
                </a>
              </div>

              {/* Hours Card */}
              <div className="bg-surface/50 border border-white/5 rounded-xl p-6 hover:border-saffron/20 transition-colors">
                <div className="w-12 h-12 rounded-full bg-saffron/10 flex items-center justify-center mb-4">
                  <Clock className="w-6 h-6 text-saffron" />
                </div>
                <h3 className="font-heading text-white text-lg mb-2">Hours</h3>
                <p className="font-body text-white/50 text-sm">
                  Mon-Fri: 9am-10pm
                </p>
              </div>
            </div>

            {/* Map Placeholder */}
            <div className="relative rounded-xl overflow-hidden aspect-video bg-surface border border-white/5">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3022.9663095343043!2d-74.00425878428698!3d40.74076794379132!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c259bf5c1654f3%3A0xc80f9cfce5383d5d!2sGoogle!5e0!3m2!1sen!2sus!4v1635956264978!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0, filter: "grayscale(100%) invert(92%) contrast(83%)" }}
                allowFullScreen=""
                loading="lazy"
                title="Cafe Irani Location"
                data-testid="contact-map"
              />
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            {success ? (
              <div data-testid="contact-success" className="text-center py-16">
                <div className="w-20 h-20 rounded-full bg-saffron/20 flex items-center justify-center mx-auto mb-6">
                  <Check className="w-10 h-10 text-saffron" />
                </div>
                <h2 className="font-heading text-3xl text-white mb-4">
                  Message <span className="italic text-saffron">Sent!</span>
                </h2>
                <p className="font-body text-white/60 mb-8">
                  Thank you for reaching out. We'll get back to you shortly.
                </p>
                <button
                  onClick={() => {
                    setSuccess(false);
                    setFormData({ name: "", email: "", subject: "", message: "" });
                  }}
                  className="px-8 py-3 rounded-full border border-white/20 text-white font-body hover:bg-white/10 transition-all"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <input
                      type="text"
                      placeholder="Your Name *"
                      data-testid="contact-name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full bg-transparent border-b border-white/20 focus:border-saffron px-0 py-4 text-white placeholder:text-white/30 focus:outline-none transition-colors"
                      required
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      placeholder="Your Email *"
                      data-testid="contact-email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full bg-transparent border-b border-white/20 focus:border-saffron px-0 py-4 text-white placeholder:text-white/30 focus:outline-none transition-colors"
                      required
                    />
                  </div>
                </div>

                <div>
                  <input
                    type="text"
                    placeholder="Subject *"
                    data-testid="contact-subject"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    className="w-full bg-transparent border-b border-white/20 focus:border-saffron px-0 py-4 text-white placeholder:text-white/30 focus:outline-none transition-colors"
                    required
                  />
                </div>

                <div>
                  <textarea
                    placeholder="Your Message *"
                    data-testid="contact-message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={6}
                    className="w-full bg-transparent border-b border-white/20 focus:border-saffron px-0 py-4 text-white placeholder:text-white/30 focus:outline-none transition-colors resize-none"
                    required
                  />
                </div>

                <button
                  type="submit"
                  data-testid="submit-contact-btn"
                  disabled={loading}
                  className="group w-full py-5 rounded-full bg-saffron text-white font-heading italic text-lg hover:bg-saffron/90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                >
                  {loading ? "Sending..." : "Send Message"}
                  <Send className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
